/**
 * VirtualJoystick - PixiJS virtual joystick for mobile controls
 *
 * A dynamic joystick that appears where the user first touches,
 * with an outer ring and inner handle for smooth direction control.
 */

import { Container, Graphics } from 'pixi.js';
import { MobileSettings, BASE_SIZES } from '../utils/MobileSettings';

/**
 * Joystick configuration
 */
export interface VirtualJoystickConfig {
  /** Outer ring diameter */
  outerDiameter: number;
  /** Inner handle diameter */
  innerDiameter: number;
  /** Opacity when inactive */
  inactiveOpacity: number;
  /** Opacity when active */
  activeOpacity: number;
  /** Whether to use dynamic positioning (appears where touched) */
  dynamicPosition: boolean;
  /** Dead zone radius as percentage (0-1) */
  deadZone: number;
}

const DEFAULT_CONFIG: VirtualJoystickConfig = {
  outerDiameter: 120,
  innerDiameter: 50,
  inactiveOpacity: 0.5,
  activeOpacity: 0.8,
  dynamicPosition: true,
  deadZone: 0.15,
};

/**
 * Joystick state
 */
export interface JoystickState {
  /** Whether the joystick is currently being touched */
  active: boolean;
  /** Normalized direction X (-1 to 1) */
  x: number;
  /** Normalized direction Y (-1 to 1) */
  y: number;
  /** Angle in radians */
  angle: number;
  /** Distance from center (0 to 1) */
  force: number;
}

/**
 * Arcade-style color palette
 */
const JOYSTICK_COLORS = {
  outerRing: 0x4ecdc4,       // Teal
  outerRingGlow: 0x00ffcc,   // Cyan glow
  innerHandle: 0x00ff88,     // Bright green
  innerHandleGlow: 0x00ffaa, // Green glow
  background: 0x1a1a2e,      // Dark blue
  active: 0xffff00,          // Yellow when active
};

/**
 * VirtualJoystick - Dynamic touchscreen joystick
 */
export class VirtualJoystick {
  /** Main container */
  public container: Container;

  /** Configuration */
  private _config: VirtualJoystickConfig;

  /** Outer ring graphics */
  private _outerRing: Graphics;

  /** Inner handle graphics */
  private _innerHandle: Graphics;

  /** Background glow */
  private _glow: Graphics;

  /** Current joystick state */
  private _state: JoystickState = {
    active: false,
    x: 0,
    y: 0,
    angle: 0,
    force: 0,
  };

  /** Touch identifier currently controlling the joystick */
  private _touchId: number | null = null;

  /** Base position (where joystick appears) */
  private _basePosition: { x: number; y: number } = { x: 0, y: 0 };

  /** Current handle position (relative to base) */
  private _handleOffset: { x: number; y: number } = { x: 0, y: 0 };

  /** Screen dimensions */
  private _screenWidth: number = 0;
  private _screenHeight: number = 0;

  /** Animation time for effects */
  private _animTime: number = 0;

  /** Touch zone (left or right half of screen) */
  private _touchZone: 'left' | 'right' = 'left';

  /** Bound event handlers */
  private _boundHandlers: {
    touchStart: (e: TouchEvent) => void;
    touchMove: (e: TouchEvent) => void;
    touchEnd: (e: TouchEvent) => void;
  };

  /** State change callback */
  private _onStateChange: ((state: JoystickState) => void) | null = null;

  constructor(config: Partial<VirtualJoystickConfig> = {}) {
    // Get settings
    const settings = MobileSettings.getInstance();
    const sizes = settings.getJoystickPixelSizes();

    this._config = {
      ...DEFAULT_CONFIG,
      outerDiameter: sizes.outer,
      innerDiameter: sizes.inner,
      ...config,
    };

    // Create container
    this.container = new Container();
    this.container.label = 'virtual-joystick';
    this.container.visible = false;

    // Create graphics layers
    this._glow = new Graphics();
    this._outerRing = new Graphics();
    this._innerHandle = new Graphics();

    this.container.addChild(this._glow);
    this.container.addChild(this._outerRing);
    this.container.addChild(this._innerHandle);

    // Draw initial state
    this._drawOuterRing();
    this._drawInnerHandle();

    // Bind event handlers
    this._boundHandlers = {
      touchStart: this._handleTouchStart.bind(this),
      touchMove: this._handleTouchMove.bind(this),
      touchEnd: this._handleTouchEnd.bind(this),
    };

    // Apply settings
    this._applySettings();

    // Listen for settings changes
    settings.addListener(() => this._applySettings());
  }

  /**
   * Apply current settings
   */
  private _applySettings(): void {
    const settings = MobileSettings.getInstance();
    const sizes = settings.getJoystickPixelSizes();

    this._config.outerDiameter = sizes.outer;
    this._config.innerDiameter = sizes.inner;
    this._config.inactiveOpacity = settings.joystickOpacity * 0.7;
    this._config.activeOpacity = settings.joystickOpacity;

    // Update touch zone based on handedness
    this._touchZone = settings.isRightHanded ? 'left' : 'right';

    // Redraw
    this._drawOuterRing();
    this._drawInnerHandle();
    this._updateOpacity();
  }

  /**
   * Draw the outer ring
   */
  private _drawOuterRing(): void {
    const g = this._outerRing;
    const radius = this._config.outerDiameter / 2;

    g.clear();

    // Outer glow
    g.circle(0, 0, radius + 4);
    g.fill({ color: JOYSTICK_COLORS.outerRingGlow, alpha: 0.2 });

    // Background fill
    g.circle(0, 0, radius);
    g.fill({ color: JOYSTICK_COLORS.background, alpha: 0.6 });

    // Outer ring border
    g.circle(0, 0, radius);
    g.stroke({ width: 3, color: JOYSTICK_COLORS.outerRing, alpha: 0.9 });

    // Inner decorative ring
    g.circle(0, 0, radius * 0.7);
    g.stroke({ width: 1, color: JOYSTICK_COLORS.outerRing, alpha: 0.3 });

    // Cross-hair lines
    g.moveTo(-radius * 0.4, 0);
    g.lineTo(-radius * 0.15, 0);
    g.stroke({ width: 2, color: JOYSTICK_COLORS.outerRing, alpha: 0.4 });
    g.moveTo(radius * 0.15, 0);
    g.lineTo(radius * 0.4, 0);
    g.stroke({ width: 2, color: JOYSTICK_COLORS.outerRing, alpha: 0.4 });
    g.moveTo(0, -radius * 0.4);
    g.lineTo(0, -radius * 0.15);
    g.stroke({ width: 2, color: JOYSTICK_COLORS.outerRing, alpha: 0.4 });
    g.moveTo(0, radius * 0.15);
    g.lineTo(0, radius * 0.4);
    g.stroke({ width: 2, color: JOYSTICK_COLORS.outerRing, alpha: 0.4 });
  }

  /**
   * Draw the inner handle
   */
  private _drawInnerHandle(): void {
    const g = this._innerHandle;
    const radius = this._config.innerDiameter / 2;

    g.clear();

    // Handle glow
    g.circle(0, 0, radius + 3);
    g.fill({ color: JOYSTICK_COLORS.innerHandleGlow, alpha: 0.3 });

    // Main handle
    g.circle(0, 0, radius);
    g.fill({ color: JOYSTICK_COLORS.innerHandle, alpha: 0.9 });

    // Highlight
    g.circle(-radius * 0.25, -radius * 0.25, radius * 0.3);
    g.fill({ color: 0xffffff, alpha: 0.4 });

    // Center dot
    g.circle(0, 0, radius * 0.2);
    g.fill({ color: 0xffffff, alpha: 0.6 });
  }

  /**
   * Update opacity based on active state
   */
  private _updateOpacity(): void {
    const opacity = this._state.active
      ? this._config.activeOpacity
      : this._config.inactiveOpacity;
    this.container.alpha = opacity;
  }

  /**
   * Handle touch start
   */
  private _handleTouchStart(e: TouchEvent): void {
    // If already tracking a touch, ignore
    if (this._touchId !== null) return;

    // Find a touch in our zone
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;
      const touchX = touch.clientX;

      // Check if touch is in our zone
      const inLeftZone = touchX < this._screenWidth / 2;
      const isOurZone = (this._touchZone === 'left' && inLeftZone) ||
        (this._touchZone === 'right' && !inLeftZone);

      if (isOurZone) {
        this._touchId = touch.identifier;

        // Set base position where touched
        if (this._config.dynamicPosition) {
          this._basePosition.x = touchX;
          this._basePosition.y = touch.clientY;
        }

        // Position joystick at touch location
        this.container.position.set(this._basePosition.x, this._basePosition.y);
        this.container.visible = true;

        // Reset handle
        this._handleOffset.x = 0;
        this._handleOffset.y = 0;
        this._innerHandle.position.set(0, 0);

        // Update state
        this._state.active = true;
        this._updateOpacity();

        // Haptic feedback
        MobileSettings.getInstance().hapticTap();

        e.preventDefault();
        break;
      }
    }
  }

  /**
   * Handle touch move
   */
  private _handleTouchMove(e: TouchEvent): void {
    if (this._touchId === null) return;

    // Find our touch
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;

      if (touch.identifier === this._touchId) {
        // Calculate offset from base
        const dx = touch.clientX - this._basePosition.x;
        const dy = touch.clientY - this._basePosition.y;

        // Calculate distance and clamp to outer ring
        const maxDist = this._config.outerDiameter / 2 - this._config.innerDiameter / 2;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const clampedDist = Math.min(dist, maxDist);

        // Calculate angle
        const angle = Math.atan2(dy, dx);

        // Calculate clamped position
        if (dist > 0) {
          this._handleOffset.x = (dx / dist) * clampedDist;
          this._handleOffset.y = (dy / dist) * clampedDist;
        } else {
          this._handleOffset.x = 0;
          this._handleOffset.y = 0;
        }

        // Update handle position
        this._innerHandle.position.set(this._handleOffset.x, this._handleOffset.y);

        // Calculate normalized state
        const normalizedDist = clampedDist / maxDist;
        const deadZone = this._config.deadZone;

        if (normalizedDist < deadZone) {
          // In dead zone
          this._state.x = 0;
          this._state.y = 0;
          this._state.force = 0;
        } else {
          // Outside dead zone - remap to 0-1 range
          const adjustedForce = (normalizedDist - deadZone) / (1 - deadZone);
          this._state.x = Math.cos(angle) * adjustedForce;
          this._state.y = Math.sin(angle) * adjustedForce;
          this._state.force = adjustedForce;
        }

        this._state.angle = angle;

        // Notify listener
        if (this._onStateChange) {
          this._onStateChange(this._state);
        }

        e.preventDefault();
        break;
      }
    }
  }

  /**
   * Handle touch end
   */
  private _handleTouchEnd(e: TouchEvent): void {
    if (this._touchId === null) return;

    // Find our touch
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;

      if (touch.identifier === this._touchId) {
        this._touchId = null;

        // Reset state
        this._state.active = false;
        this._state.x = 0;
        this._state.y = 0;
        this._state.force = 0;

        // Animate handle back to center
        this._handleOffset.x = 0;
        this._handleOffset.y = 0;
        this._innerHandle.position.set(0, 0);

        // Hide if dynamic positioning
        if (this._config.dynamicPosition) {
          this.container.visible = false;
        }

        this._updateOpacity();

        // Notify listener
        if (this._onStateChange) {
          this._onStateChange(this._state);
        }

        e.preventDefault();
        break;
      }
    }
  }

  /**
   * Start listening for touch events
   */
  start(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('touchstart', this._boundHandlers.touchStart, { passive: false });
    canvas.addEventListener('touchmove', this._boundHandlers.touchMove, { passive: false });
    canvas.addEventListener('touchend', this._boundHandlers.touchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this._boundHandlers.touchEnd, { passive: false });
  }

  /**
   * Stop listening for touch events
   */
  stop(canvas: HTMLCanvasElement): void {
    canvas.removeEventListener('touchstart', this._boundHandlers.touchStart);
    canvas.removeEventListener('touchmove', this._boundHandlers.touchMove);
    canvas.removeEventListener('touchend', this._boundHandlers.touchEnd);
    canvas.removeEventListener('touchcancel', this._boundHandlers.touchEnd);

    // Reset state
    this._touchId = null;
    this._state.active = false;
    this._state.x = 0;
    this._state.y = 0;
    this._state.force = 0;
    this.container.visible = false;
  }

  /**
   * Set state change callback
   */
  onStateChange(callback: (state: JoystickState) => void): void {
    this._onStateChange = callback;
  }

  /**
   * Get current state
   */
  getState(): JoystickState {
    return { ...this._state };
  }

  /**
   * Get current angle
   */
  getAngle(): number {
    return this._state.angle;
  }

  /**
   * Check if active
   */
  isActive(): boolean {
    return this._state.active;
  }

  /**
   * Update joystick visuals
   */
  update(dt: number): void {
    this._animTime += dt;

    if (!this._state.active) return;

    // Subtle pulsing effect when active
    const pulse = Math.sin(this._animTime * 4) * 0.05 + 1;
    this._innerHandle.scale.set(pulse);

    // Update glow based on force
    this._updateGlow();
  }

  /**
   * Update glow effect based on force
   */
  private _updateGlow(): void {
    const g = this._glow;
    const outerRadius = this._config.outerDiameter / 2;
    const force = this._state.force;

    g.clear();

    if (force > 0.1) {
      const glowIntensity = force * 0.4;
      const glowColor = this._state.active ? JOYSTICK_COLORS.active : JOYSTICK_COLORS.outerRingGlow;

      // Direction indicator glow
      const glowX = this._handleOffset.x * 1.2;
      const glowY = this._handleOffset.y * 1.2;

      g.circle(glowX, glowY, outerRadius * 0.3 * force);
      g.fill({ color: glowColor, alpha: glowIntensity });
    }
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, height: number): void {
    this._screenWidth = width;
    this._screenHeight = height;

    // Update fixed position if not using dynamic positioning
    if (!this._config.dynamicPosition) {
      const settings = MobileSettings.getInstance();
      const padding = BASE_SIZES.edgePadding;
      const radius = this._config.outerDiameter / 2;

      if (settings.isRightHanded) {
        // Joystick on left
        this._basePosition.x = padding + radius;
      } else {
        // Joystick on right
        this._basePosition.x = width - padding - radius;
      }

      this._basePosition.y = this._screenHeight - padding - radius;
      this.container.position.set(this._basePosition.x, this._basePosition.y);
    }
  }

  /**
   * Show/hide joystick
   */
  setVisible(visible: boolean): void {
    if (!this._config.dynamicPosition) {
      this.container.visible = visible;
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this._glow.destroy();
    this._outerRing.destroy();
    this._innerHandle.destroy();
    this.container.destroy({ children: true });
    this._onStateChange = null;
  }
}
